import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flat-loader.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-link.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/fonts/fonts.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { prettyOrderStatus } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { ILSFile } from '../../db/ils-files.js';
import type { ILSUser } from '../../db/ils-user.js';
import {
  createInvoice,
  getIsRefundableForOrderID,
  getRefundAmountByOrderID,
  manuallySetOrderRevoked,
  type Order,
} from '../../db/order.js';
import { orderStatusIcon } from './orderStatusIcon.js';

// To calc the best order state, order of the items is important!
const compareArr = ['issued', 'expired', 'canceled', 'revoked', 'failed', 'refund', 'paid', 'moved'];

type NarrowedOrder = NarrowedEntity<
  Order,
  {
    status: 1;
    products: { product: { _id: 1; name: 1; reference: 1 } };
    createdAt: 1;
    name: 1;
    amount: 1;
    _id: 1;
    revokedAt: 1;
    invoices: { stripeID: 1; reference: 1 };
  }
>;

interface ProductOrderGroup {
  productId: string;
  productName: string;
  orders: NarrowedOrder[];
}

@customElement('ils-dashboard-order-item')
export class ILSDashboardOrderItem extends ChemistryLitElement {
  @property({ type: Boolean }) expanded = false;
  @property() productGroup?: ProductOrderGroup;
  @state() private selectedOrderId?: string;
  @state() private readonly user = new RXController(this, currentUser<ILSUser>());
  @state() private isRefundable = false;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    if (!this.productGroup) return;
    this.selectedOrderId = this.productGroup.orders
      .slice(1)
      .reduce(
        (acc, o) => (compareArr.indexOf(acc.status) > compareArr.indexOf(o.status) ? acc : o),
        this.productGroup.orders[0]!,
      )._id;

    this.isRefundable = await getIsRefundableForOrderID(this.selectedOrderId)();
  }

  override render() {
    if (!this.productGroup) return nothing;

    return html`
      <x-basic-card>
        <x-flex>
          <x-flex
            horizontal
            space-between
            padding="md"
            crossaxis-center
            @click=${() => this.toggleExpanded()}
            ${css({ cursor: 'pointer' })}
          >
            <x-h4>${this.productGroup.productName}</x-h4>
            <x-flex horizontal space="xs" crossaxis-center>
              ${repeat(
                this.productGroup.orders,
                order => order._id,
                order => this.renderOrderStatusIcon(order),
              )}
            </x-flex>
          </x-flex>
          ${this.expanded ? this.renderExpandedContent() : nothing}
        </x-flex>
      </x-basic-card>
    `;
  }

  private toggleExpanded() {
    this.dispatchEvent(
      new CustomEvent('click-order-item', {
        detail: { orderId: this.productGroup?.orders[0]?._id },
        composed: true,
        bubbles: true,
        cancelable: true,
      }),
    );
  }

  private renderOrderStatusIcon(order: NarrowedOrder): Renderable {
    return html`
      <div
        @click=${(e: Event) => {
          e.stopPropagation();
          this.selectOrder(order._id);
        }}
        ${css({ cursor: 'pointer' })}
      >
        ${orderStatusIcon(this as any, order.status, {
          isSelected: this.selectedOrderId === order._id,
          title: `${prettyOrderStatus(order.status)} am: ${(order.createdAt as DateTime).toLocaleString(
            DateTime.DATE_SHORT,
          )}`,
        })}
      </div>
    `;
  }

  private async selectOrder(orderId: string) {
    this.selectedOrderId = orderId;
    if (orderId) {
      this.isRefundable = await getIsRefundableForOrderID(orderId)();
    }
    if (!this.expanded) this.toggleExpanded();
    this.requestUpdate();
  }

  private renderExpandedContent(): Renderable {
    const selectedOrder = this.selectedOrderId
      ? this.productGroup?.orders.find(order => order._id === this.selectedOrderId)
      : this.productGroup?.orders[0];

    if (!selectedOrder) return nothing;

    return html`
      <x-flex ${css({ borderTop: `1px solid ${this.colors.neutral.shade(50)}` })}>
        <x-flex horizontal space-between padding="md">
          <x-text>
            ${selectedOrder.status !== 'revoked' ? prettyOrderStatus(selectedOrder.status) : prettyOrderStatus('paid')}
            am: ${(selectedOrder.createdAt as DateTime).toLocaleString(DateTime.DATE_SHORT)}
          </x-text>
          ${selectedOrder.revokedAt
            ? html`
                <x-text ${css({ color: this.colors.tone.error })}>
                  Storniert am: ${DateTime.fromJSDate(selectedOrder.revokedAt).toLocaleString(DateTime.DATE_SHORT)}
                </x-text>
              `
            : nothing}
        </x-flex>
        <x-flex horizontal ${css({ borderTop: `1px solid ${this.colors.neutral.shade(50)}` })}>
          ${selectedOrder.status !== 'paid'
            ? nothing
            : html` <x-flex
                flex
                center
                crossaxis-center
                padding="md"
                ${acss({ cursor: 'pointer', '&:hover': { background: this.colors.neutral.shade(50) } })}
                @click=${async () => {
                  await this.onOrderDownloadInvoice(selectedOrder);
                }}
              >
                <x-text> Rechnung herunterladen </x-text>
              </x-flex>`}
          ${this.isRefundable
            ? html` <x-flex
                flex
                center
                crossaxis-center
                padding="md"
                ${acss({
                  cursor: 'pointer',
                  '&:hover': { background: this.colors.tone.error.shade(500) },
                  background: this.colors.tone.error,
                  color: this.colors.on.tone.error,
                })}
                @click=${async () => {
                  await this.onOrderCancel(selectedOrder);
                }}
              >
                <x-text> Stornieren </x-text>
              </x-flex>`
            : nothing}
        </x-flex>
      </x-flex>
    `;
  }

  private async onOrderDownloadInvoice(order: NarrowedOrder) {
    const user = this.user.value;
    if (!user) return XDialog.alert('Kein Nutzer angemeldet!');
    const invoiceID = order.invoices[0]?.reference || (await XDialog.waitFor(createInvoice(order._id)()));
    if (invoiceID) {
      const invoice = await getByID(ILSFile, invoiceID)(selectionSet(() => ILSFile, 2));
      if (invoice) goTo(invoice.getServeLink(), { target: '_blank' });
      else return XDialog.alert('Es gab ein Problem beim anzeigen der Rechnung. Bitte wenden Sie sich an uns!');
    } else {
      return XDialog.alert('Es gibt noch keine Rechnung für diese Bestellung');
    }
  }

  private async onOrderCancel(order: NarrowedOrder) {
    const refundAmount = await XDialog.waitFor<number>(getRefundAmountByOrderID(order._id)());
    const orderAmount = order.amount;
    const formatter = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    });

    if (
      !(await XDialog.confirm(
        html`
          <x-flex space="md">
            <x-h4> Bestellung unwiderruflich stornieren? </x-h4>
            <x-text>
              Von den gezahlten ${formatter.format(orderAmount / 100)} erhalten Sie
              <b>${formatter.format(refundAmount / 100)}</b> zurück.
            </x-text>
          </x-flex>
        `,
        {
          highlightDecline: true,
        },
      ))
    )
      return;

    return XDialog.waitFor(
      manuallySetOrderRevoked(order._id)()
        .then(() => {
          // optimistically update order
          order.status = 'revoked';
          order.revokedAt = new Date() as any;
          this.requestUpdate();

          return XSnackbar.show('Bestellung erfolgreich storniert');
        })
        .catch((e: any) => XDialog.alert(e.message)),
    );
  }
}
